import { List, Map } from 'immutable';
import { createSelector } from 'reselect';
import { ChannelKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { extractAbbreviatedName } from 'flow/utils';
import { sessionIsArchived, sessionIsNotArchived } from 'flow/utils/chat-utils';
import { calcTotalUnreadChannelMessages } from 'flow/utils/pbx-utils';
import { newestFirstSorter } from 'flow/utils/sorter-utils';
import { selectContact } from './ExtensionSelectors';
import { selectOwnChatUserKey } from './own-extension-selectors';

const selectChannels = (state: State) => state.channels.get('channels');

const selectChannelChatSessions = (state: State, channelKey: ChannelKey) =>
    state.channels.getIn(['channels', channelKey, 'chatSessionDTOs'], Map());

const selectLastTypingUserKey = (
    state: State,
    channelKey: ChannelKey,
    chatSessionKey: ChatSessionKey,
) =>
    state.channels
        .getIn([
            'channels',
            channelKey,
            'chatSessionDTOs',
            chatSessionKey,
            'typingUserKeys',
        ])
        ?.last();

export const selectLastTypingUserName = (
    state: State,
    channelKey: ChannelKey,
    chatSessionKey: ChatSessionKey,
) => {
    const lastTypingUserKey = selectLastTypingUserKey(
        state,
        channelKey,
        chatSessionKey,
    );
    return lastTypingUserKey
        ? extractAbbreviatedName(selectContact(state, lastTypingUserKey))
        : '';
};

export const selectChannelExists = (state: State, channelKey: ChannelKey) =>
    state.channels.hasIn(['channels', channelKey]);

export const selectChannelMembers = (state: State, channelKey: ChannelKey) =>
    state.channels.getIn(['channels', channelKey, 'channelMemberDTOs'], List());

export const selectChannelActiveProfile = (
    state: State,
    channelKey: ChannelKey,
) => state.channels.getIn(['channels', channelKey, 'activeProfile']);

export const selectChannelName = (state: State, channelKey: ChannelKey) =>
    state.channels.getIn(['channels', channelKey, 'channelName']);

export const selectChannelEditable = (state: State, channelKey: ChannelKey) =>
    state.channels.getIn(['channels', channelKey, 'editable'], false);

export const selectChannelUnreadMessagesCount = (
    state: State,
    channelKey: ChannelKey,
) =>
    state.channels.getIn(['channels', channelKey, 'numberOfUnreadMessages'], 0);

export const selectChannelActiveProfileIsAvailable = (
    state: State,
    channelKey: ChannelKey,
) =>
    state.channels.getIn([
        'channels',
        channelKey,
        'activeProfile',
        'available',
    ]);

export const selectWidgetMessages = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => state.channels.getIn(['widgetMessages', chatSessionKey]);

const selectLastMessage = (state: State, chatSessionKey: ChatSessionKey) =>
    selectWidgetMessages(state, chatSessionKey)?.last();

export const selectLastMessageIsRead = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => selectLastMessage(state, chatSessionKey)?.get('read', true);

export const selectLastMessageKey = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => selectLastMessage(state, chatSessionKey)?.get('key');

export const selectNumberUnreadChannelMessages = createSelector(
    [selectChannels, selectOwnChatUserKey],
    (channels, chatUserKey): number =>
        calcTotalUnreadChannelMessages(channels, chatUserKey),
);

export const selectArchivedChatSessions = createSelector(
    [selectChannelChatSessions],
    (sessions) =>
        sessions
            .filter(sessionIsArchived)
            .sortBy(
                (session) => session.getIn(['latestMessage', 'sent']),
                newestFirstSorter,
            ),
);

export const selectOpenChatSessions = createSelector(
    [selectChannelChatSessions],
    (sessions) =>
        sessions
            .filter(sessionIsNotArchived)
            .sortBy(
                (session) => session.getIn(['latestMessage', 'sent']),
                newestFirstSorter,
            ),
);

export const selectOwnUserIsMemberOfChannel = createSelector(
    [selectChannelMembers, selectOwnChatUserKey],
    (channelMembers, ownChatUserKey) =>
        channelMembers.some(
            (member) => member.get('chatUserKey') === ownChatUserKey,
        ),
);

export const makeSelectOwnUserIsLoggedInToChannel = () =>
    createSelector(
        [selectChannelMembers, selectOwnChatUserKey],
        (channelMembers, ownChatUserKey) =>
            channelMembers.some(
                (member) =>
                    member?.get('chatUserKey') === ownChatUserKey &&
                    member?.get('loggedIn'),
            ),
    );

export const makeSelectAgentsLoggedInToChannelCount = () =>
    createSelector(
        [selectChannelMembers],
        (channelMembers) =>
            channelMembers?.filter((member) => member.get('loggedIn'))?.size,
    );
